import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { supabase } from "../../lib/supabase";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDarkMode } from "../../contexts/DarkModeContext";

// Floating Dark Mode Toggle Component
function FloatingDarkModeToggle() {
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.button
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      onClick={toggleDarkMode}
      className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
      aria-label="Toggle dark mode"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ duration: 0.3, delay: 1 }}
    >
      {isDarkMode ? (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
            clipRule="evenodd"
          />
        </svg>
      ) : (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </motion.button>
  );
}

const AdminInvite = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const [tokens, setTokens] = useState({ accessToken: null, refreshToken: null });
  const navigate = useNavigate();

  // Helper function to parse URL fragments (after #)
  const parseUrlFragment = () => {
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    return params;
  };

  // Helper function to extract tokens from various sources
  const extractTokens = () => {
    let accessToken = null;
    let refreshToken = null;
    let tokenType = null;

    // Method 1: Check query parameters (?access_token=...)
    accessToken = searchParams.get("access_token") || searchParams.get("token");
    refreshToken = searchParams.get("refresh_token");
    tokenType = searchParams.get("type");

    // Method 2: Check URL fragments (#access_token=...)
    if (!accessToken) {
      const fragmentParams = parseUrlFragment();
      accessToken = fragmentParams.get("access_token") || fragmentParams.get("token");
      refreshToken = fragmentParams.get("refresh_token");
      tokenType = fragmentParams.get("type");
    }

    // Method 3: Check for single token parameter (some Supabase setups use just 'token')
    if (!accessToken) {
      const singleToken = searchParams.get("token") || parseUrlFragment().get("token");
      if (singleToken) {
        accessToken = singleToken;
      }
    }

    return { accessToken, refreshToken, tokenType };
  };

  useEffect(() => {
    const { accessToken, refreshToken, tokenType } = extractTokens();
    
    setTokens({ accessToken, refreshToken });
    
    if (!accessToken) {
      setMessage("Invalid invitation link. Please contact an administrator.");
      setIsSuccess(false);
    } else {
      setMessage(""); // Clear any previous error messages
    }
  }, [searchParams]);

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    // Validate passwords match
    if (password !== confirmPassword) {
      setMessage("Passwords do not match.");
      setIsSuccess(false);
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setMessage("Password must be at least 6 characters long.");
      setIsSuccess(false);
      setLoading(false);
      return;
    }

    try {
      const { accessToken, refreshToken } = tokens;

      if (!accessToken) {
        throw new Error("Invalid invitation link");
      }

      // Set the session using the tokens from the invitation
      const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      });

      if (sessionError) {
        throw sessionError;
      }

      // Update the user's password
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      // Success
      setIsSuccess(true);
      setMessage("Password set successfully! Redirecting to admin dashboard...");
      
      // Redirect to admin dashboard after successful password setup
      setTimeout(() => {
        navigate("/admin/dashboard");
      }, 2000);

    } catch (error) {
      console.error("Password setup error:", error);
      setIsSuccess(false);
      
      if (error.message.includes("expired")) {
        setMessage("Invitation link has expired. Please request a new invitation.");
      } else if (error.message.includes("invalid")) {
        setMessage("Invalid invitation link. Please contact an administrator.");
      } else {
        setMessage(`Setup failed: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
              Set Admin Password
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
              Complete your admin account setup
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSetPassword}>
            <div className="space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  New Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-aifund focus:border-aifund"
                  placeholder="Enter new password (min 6 characters)"
                  minLength={6}
                />
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-aifund focus:border-aifund"
                  placeholder="Confirm new password"
                  minLength={6}
                />
              </div>
            </div>

            {message && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-md ${
                  isSuccess
                    ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800"
                    : "bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800"
                }`}
              >
                {message}
              </motion.div>
            )}

            <div>
              <motion.button
                type="submit"
                disabled={loading || !tokens.accessToken}
                whileHover={{ scale: loading ? 1 : 1.02 }}
                whileTap={{ scale: loading ? 1 : 0.98 }}
                className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white transition-colors duration-200 ${
                  loading || !tokens.accessToken
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-aifund hover:bg-aifund-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-aifund"
                }`}
              >
                {loading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Setting up account...
                  </div>
                ) : (
                  "Set Password & Continue"
                )}
              </motion.button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Password must be at least 6 characters long
              </p>
            </div>
          </form>
        </motion.div>
      </div>
      
      {/* Floating Dark Mode Toggle */}
      <FloatingDarkModeToggle />
    </div>
  );
};

export default AdminInvite;